<template>
  <div class="swa">
    <!-- Sign-in Required -->
    <div v-if="!hasSession" class="swa-signin">
      <i class="bi bi-lock"></i>
      <div class="swa-signin__body">
        <strong>Sign in required</strong>
        <p>Please sign in to access severe weather analysis data.</p>
      </div>
      <a class="swa-btn swa-btn--primary" href="#" @click.prevent="showSignIn">
        <i class="bi bi-box-arrow-in-right"></i> Sign In
      </a>
    </div>

    <template v-else>
      <!-- Controls Bar -->
      <div class="swa-controls">
        <!-- Location -->
        <div class="swa-control-group">
          <label class="swa-label">Location</label>
          <div class="swa-location-row">
            <input type="text" class="swa-input swa-input--location" v-model="locationText"
              @keyup.enter="geocodeLocation" placeholder="City, address, or lat,lon..." />
            <button class="swa-btn swa-btn--icon" @click="geocodeLocation" :disabled="isGeocoding"
              title="Search location">
              <i class="bi" :class="isGeocoding ? 'bi-arrow-repeat swa-spin' : 'bi-search'"></i>
            </button>
            <button class="swa-btn swa-btn--icon" @click="useCurrentLocation" :disabled="isGeocoding"
              title="Use current location">
              <i class="bi bi-geo"></i>
            </button>
          </div>
          <div v-if="location" class="swa-location-display">
            <i class="bi bi-geo-alt-fill"></i>
            {{ location.name || `${location.lat?.toFixed(4)}, ${location.lon?.toFixed(4)}` }}
          </div>
        </div>

        <!-- Date Range -->
        <div class="swa-control-group">
          <label class="swa-label">Date Range</label>
          <div class="swa-date-row">
            <input type="date" class="swa-input swa-input--date" v-model="startDate" min="2026-02-01" />
            <span class="swa-date-sep">to</span>
            <input type="date" class="swa-input swa-input--date" v-model="endDate" :min="startDate" />
          </div>
          <div class="swa-quick-dates">
            <a href="#" @click.prevent="setDateRange('yesterday')">Yesterday</a>
            <a href="#" @click.prevent="setDateRange('today')">Today</a>
            <a href="#" @click.prevent="setDateRange('3days')">Last 3 Days</a>
            <a href="#" @click.prevent="setDateRange('week')">Last Week</a>
          </div>
        </div>

        <!-- Max Distance -->
        <div class="swa-control-group">
          <label class="swa-label">Max Distance</label>
          <select class="swa-input swa-input--select" v-model.number="maxDistanceMiles">
            <option v-for="d in distanceOptions" :key="d.value" :value="d.value">{{ d.label }}</option>
          </select>
        </div>

        <!-- Units -->
        <div class="swa-control-group">
          <label class="swa-label">Units</label>
          <select class="swa-input swa-input--select" v-model="unitGroup">
            <option value="us">US</option>
            <option value="metric">Metric</option>
            <option value="uk">UK</option>
            <option value="base">SI</option>
          </select>
        </div>

        <!-- Analyze Button -->
        <div class="swa-control-group swa-control-group--action">
          <button class="swa-btn swa-btn--primary swa-btn--analyze" @click="analyze"
            :disabled="!canAnalyze || isLoading">
            <i class="bi" :class="isLoading ? 'bi-arrow-repeat swa-spin' : 'bi-lightning-charge-fill'"></i>
            {{ isLoading ? 'Analyzing...' : 'Analyze' }}
          </button>
        </div>
      </div>

      <!-- Error -->
      <div v-if="error" class="swa-error">
        <i class="bi bi-exclamation-triangle-fill"></i> {{ error }}
      </div>

      <!-- Skeleton loading UI -->
      <div v-if="isLoading && !hasResults" class="swa-skeleton">
        <div class="swa-section">
          <h3 class="swa-section__title"><i class="bi bi-speedometer2"></i> Severe Weather Summary</h3>
          <div class="swa-kpi-grid">
            <div v-for="n in 6" :key="n" class="swa-kpi swa-skeleton-card">
              <div class="swa-skeleton-bar swa-skeleton-bar--icon"></div>
              <div class="swa-skeleton-bar swa-skeleton-bar--value"></div>
              <div class="swa-skeleton-bar swa-skeleton-bar--label"></div>
            </div>
          </div>
        </div>
        <div class="swa-section">
          <h3 class="swa-section__title"><i class="bi bi-bar-chart-fill"></i> Observation Sources</h3>
          <div class="swa-comparison-table-wrap">
            <table class="swa-table">
              <thead>
                <tr>
                  <th>Element</th><th>Stations — Hourly</th><th>Stations — Daily</th><th>Radar/Remote</th><th>ERA5</th><th>Radar (V1)</th><th>Delta</th><th>Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="n in 5" :key="n">
                  <td v-for="c in 8" :key="c"><div class="swa-skeleton-bar swa-skeleton-bar--cell"></div></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="swa-section">
          <h3 class="swa-section__title"><i class="bi bi-calendar3"></i> Daily Breakdown</h3>
          <div v-for="n in 2" :key="n" class="swa-skeleton-day">
            <div class="swa-skeleton-bar swa-skeleton-bar--day-header"></div>
            <div class="swa-skeleton-bar swa-skeleton-bar--day-body"></div>
          </div>
        </div>
      </div>

      <!-- Results -->
      <div v-if="hasResults" class="swa-results">
        <!-- KPI Summary -->
        <div class="swa-section">
          <h3 class="swa-section__title"><i class="bi bi-speedometer2"></i> Severe Weather Summary</h3>
          <div class="swa-kpi-grid">
            <div class="swa-kpi" :class="severeOccurred ? 'swa-kpi--danger' : 'swa-kpi--safe'">
              <div class="swa-kpi__icon">
                <i class="bi" :class="severeOccurred ? 'bi-exclamation-triangle-fill' : 'bi-shield-check'"></i>
              </div>
              <div class="swa-kpi__value">{{ severeOccurred ? 'YES' : 'NO' }}</div>
              <div class="swa-kpi__label">Severe Weather Detected</div>
            </div>
            <div class="swa-kpi" :class="kpiSeverityClass">
              <div class="swa-kpi__icon"><i class="bi bi-thermometer-high"></i></div>
              <div class="swa-kpi__value">{{ severityScore }}/10</div>
              <div class="swa-kpi__label">Severity Score</div>
            </div>
            <div class="swa-kpi">
              <div class="swa-kpi__icon"><i class="bi bi-cloud-rain-heavy-fill"></i></div>
              <div class="swa-kpi__value">{{ formatVal(kpis.maxPrecip, 'precip') }}</div>
              <div class="swa-kpi__label">Max Precipitation</div>
            </div>
            <div class="swa-kpi">
              <div class="swa-kpi__icon"><i class="bi bi-wind"></i></div>
              <div class="swa-kpi__value">{{ formatVal(kpis.maxWindGust, 'windgust') }}</div>
              <div class="swa-kpi__label">Max Wind Gust</div>
            </div>
            <div class="swa-kpi">
              <div class="swa-kpi__icon"><i class="bi bi-lightning-fill"></i></div>
              <div class="swa-kpi__value">{{ formatVal(kpis.maxLightningRisk, 'pct') }}</div>
              <div class="swa-kpi__label">Max Lightning Risk</div>
            </div>
            <div class="swa-kpi">
              <div class="swa-kpi__icon"><i class="bi bi-lightning-charge"></i></div>
              <div class="swa-kpi__value">{{ formatVal(kpis.maxLightningDensity, 'density') }}</div>
              <div class="swa-kpi__label">Max Lightning Density</div>
            </div>
            <div class="swa-kpi">
              <div class="swa-kpi__icon"><i class="bi bi-circle-fill" style="color:#7c3aed"></i></div>
              <div class="swa-kpi__value">{{ formatVal(kpis.maxHailRisk, 'pct') }}</div>
              <div class="swa-kpi__label">Max Hail Risk</div>
            </div>
            <div class="swa-kpi">
              <div class="swa-kpi__icon"><i class="bi bi-bullseye" style="color:#7c3aed"></i></div>
              <div class="swa-kpi__value">{{ formatVal(kpis.maxHailSize, 'hailsize') }}</div>
              <div class="swa-kpi__label">Max Hail Size</div>
            </div>
            <div class="swa-kpi">
              <div class="swa-kpi__icon"><i class="bi bi-exclamation-diamond-fill"></i></div>
              <div class="swa-kpi__value">{{ formatVal(kpis.maxSevereRisk, 'pct') }}</div>
              <div class="swa-kpi__label">Max Severe Risk</div>
            </div>
            <div class="swa-kpi">
              <div class="swa-kpi__icon"><i class="bi bi-moisture"></i></div>
              <div class="swa-kpi__value">{{ formatVal(kpis.maxReflectivity, 'dbz') }}</div>
              <div class="swa-kpi__label">Max Reflectivity</div>
            </div>
          </div>
        </div>

        <!-- Active Alerts -->
        <div v-if="alerts && alerts.length" class="swa-section">
          <h3 class="swa-section__title"><i class="bi bi-bell-fill"></i> Weather Alerts ({{ alerts.length }})</h3>
          <div class="swa-alerts">
            <div v-for="(alert, idx) in alerts" :key="idx"
              class="swa-alert" :class="'swa-alert--' + getAlertCategory(alert)">
              <div class="swa-alert__header">
                <i class="bi" :class="getAlertIcon(alert)"></i>
                <strong>{{ alert.event }}</strong>
                <span class="swa-alert__time">{{ alert.onset }} – {{ alert.ends }}</span>
              </div>
              <p v-if="alert.headline" class="swa-alert__headline">{{ alert.headline }}</p>
              <details v-if="alert.description" class="swa-alert__details">
                <summary>Details</summary>
                <p>{{ alert.description }}</p>
              </details>
            </div>
          </div>
        </div>

        <!-- Observation Sources & Comparison -->
        <div class="swa-section">
          <h3 class="swa-section__title"><i class="bi bi-arrow-left-right"></i> Observation Sources &amp; Comparison</h3>

          <!-- Source legend -->
          <div class="swa-source-legend">
            <div class="swa-source-legend__item">
              <span class="swa-source-dot swa-source-dot--station"></span>
              <strong>Stations — Hourly</strong> — Weighted average from nearby weather station gauges (sub-hourly)
            </div>
            <div class="swa-source-legend__item">
              <span class="swa-source-dot swa-source-dot--dailyobs"></span>
              <strong>Stations — Daily</strong> — Daily-reporting ground stations, COOP/CoCoRaHS (8am–8am cycle)
              <span v-if="isLoadingDailyObs" class="swa-loading-inline"><i class="bi bi-arrow-repeat swa-spin"></i></span>
            </div>
            <div class="swa-source-legend__item">
              <span class="swa-source-dot swa-source-dot--radar"></span>
              <strong>Radar/Remote</strong> — MRMS radar-derived precipitation estimate at the location
            </div>
            <div class="swa-source-legend__item">
              <span class="swa-source-dot swa-source-dot--era5"></span>
              <strong>ERA5</strong> — ECMWF global reanalysis dataset (gridded, ~30 km)
            </div>
            <div class="swa-source-legend__item">
              <span class="swa-source-dot swa-source-dot--v1forecast"></span>
              <strong>Radar (V1)</strong> — Radar/remote result without usev2forecast (baseline)
            </div>
          </div>

          <!-- Overall comparison -->
          <div class="swa-comparison-table-wrap">
            <table class="swa-table">
              <thead>
                <tr>
                  <th>Element</th>
                  <th>Stations — Hourly</th>
                  <th>Stations — Daily</th>
                  <th>Radar/Remote</th>
                  <th>ERA5</th>
                  <th>Radar (V1)</th>
                  <th>Delta</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="c in comparisonRows" :key="c.element">
                  <td class="swa-table__el">{{ c.label }}</td>
                  <td>{{ c.station }}</td>
                  <td>{{ c.dailyObs }}</td>
                  <td>{{ c.remote }}</td>
                  <td>{{ c.era5 }}</td>
                  <td>{{ c.v1forecast }}</td>
                  <td :class="c.deltaClass">{{ c.delta }}</td>
                  <td class="swa-table__note">{{ c.note }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Per-station breakdown -->
          <div class="swa-station-breakdown">
            <h4 class="swa-subsection-title">
              <i class="bi bi-broadcast-pin"></i> Individual Station Measurements
              <span v-if="isLoadingStations" class="swa-loading-inline"><i class="bi bi-arrow-repeat swa-spin"></i> Loading...</span>
            </h4>
            <p class="swa-station-breakdown__desc">15-minute interval data from each contributing station. Compare individual gauge readings with the blended and radar values above.</p>
            <div class="swa-comparison-table-wrap">
              <table v-if="stationSummaries.length" class="swa-table">
                <thead>
                  <tr>
                    <th>Station</th>
                    <th>Distance</th>
                    <th>Precip Total</th>
                    <th>Max Wind</th>
                    <th>Max Gust</th>
                    <th>Records</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="s in stationSummaries" :key="s.id" :class="{ 'swa-row--loading': !s.loaded }">
                    <td class="swa-table__el">
                      <i class="bi bi-broadcast" style="color:#3b82f6"></i> {{ s.name }}
                      <div class="swa-station-id">{{ s.id }}</div>
                    </td>
                    <td>{{ s.distance }} {{ distUnit }}</td>
                    <td>{{ s.loaded ? formatVal(s.totalPrecip, 'precip') : '...' }}</td>
                    <td>{{ s.loaded ? formatVal(s.maxWindSpeed, 'windspeed') : '...' }}</td>
                    <td>{{ s.loaded ? formatVal(s.maxWindGust, 'windgust') : '...' }}</td>
                    <td>{{ s.loaded ? s.minuteCount : '...' }}</td>
                  </tr>
                  <!-- Blended + Radar summary rows for context -->
                  <tr class="swa-table__summary-row">
                    <td class="swa-table__el" colspan="2"><span class="swa-summary-label"><span class="swa-source-dot swa-source-dot--station"></span> Blended Hourly Value</span></td>
                    <td>{{ comparisonRows.length ? comparisonRows[0].station : '—' }}</td>
                    <td>{{ comparisonRows.length >= 3 ? comparisonRows[2].station : '—' }}</td>
                    <td>{{ comparisonRows.length >= 4 ? comparisonRows[3].station : '—' }}</td>
                    <td class="swa-table__note">Weighted avg</td>
                  </tr>
                  <tr class="swa-table__summary-row">
                    <td class="swa-table__el" colspan="2"><span class="swa-summary-label"><span class="swa-source-dot swa-source-dot--radar"></span> Radar/Remote</span></td>
                    <td>{{ comparisonRows.length ? comparisonRows[0].remote : '—' }}</td>
                    <td colspan="3" class="swa-table__note">Radar does not observe wind directly</td>
                  </tr>
                </tbody>
              </table>
              <p v-else-if="!isLoadingStations" class="swa-no-data">No contributing stations found.</p>
            </div>
          </div>

          <!-- Daily Obs Stations breakdown -->
          <div class="swa-station-breakdown">
            <h4 class="swa-subsection-title">
              <i class="bi bi-thermometer-half" style="color:#0891b2"></i> Daily Observation Stations
              <span v-if="isLoadingDailyObs" class="swa-loading-inline"><i class="bi bi-arrow-repeat swa-spin"></i> Loading...</span>
            </h4>
            <p class="swa-station-breakdown__desc">Daily-reporting stations (8am–8am cycle). Precip uses next-day offset to align with calendar day. These are typically COOP/CoCoRaHS ground-truth gauges.</p>
            <div class="swa-comparison-table-wrap">
              <table v-if="dailyObsStations.length" class="swa-table">
                <thead>
                  <tr>
                    <th>Station</th>
                    <th>Distance</th>
                    <th v-for="dr in dailyObsStations[0]?.dayReadings || []" :key="dr.date">{{ dr.date }}</th>
                    <th>Total Precip</th>
                    <th>Max Wind</th>
                    <th>Max Gust</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="s in dailyObsStations" :key="s.id" :class="{ 'swa-row--loading': !s.loaded }">
                    <td class="swa-table__el">
                      <i class="bi bi-thermometer-half" style="color:#0891b2"></i> {{ s.name }}
                      <div class="swa-station-id">{{ s.id }}</div>
                    </td>
                    <td>{{ s.distance }} {{ distUnit }}</td>
                    <td v-for="dr in s.dayReadings" :key="dr.date">
                      {{ formatVal(dr.precip, 'precip') }}
                      <span v-if="dr.tempmax != null" class="swa-daily-temp">{{ dr.tempmax?.toFixed(0) }}° / {{ dr.tempmin?.toFixed(0) }}°</span>
                    </td>
                    <td v-if="!s.loaded" :colspan="3">...</td>
                    <template v-else>
                      <td><strong>{{ formatVal(s.totalPrecip, 'precip') }}</strong></td>
                      <td>{{ formatVal(s.maxWind, 'windspeed') }}</td>
                      <td>{{ formatVal(s.maxGust, 'windgust') }}</td>
                    </template>
                  </tr>
                </tbody>
              </table>
              <p v-else-if="!isLoadingDailyObs" class="swa-no-data">No daily observation stations found.</p>
            </div>
          </div>

          <!-- Source Agreement Summary -->
          <div v-if="sourceAgreement" class="swa-agreement-panel">
            <h4 class="swa-subsection-title"><i class="bi bi-clipboard-data"></i> Source Agreement Summary</h4>
            <div class="swa-agreement-grid">
              <div v-if="sourceAgreement.precip" class="swa-agreement-card" :class="'swa-agreement--' + sourceAgreement.precip.agreement">
                <div class="swa-agreement-card__title">Precipitation</div>
                <div class="swa-agreement-card__range">{{ sourceAgreement.precip.min }} – {{ sourceAgreement.precip.max }} {{ sourceAgreement.precip.unit }}</div>
                <div class="swa-agreement-card__avg">Avg: {{ sourceAgreement.precip.avg }} {{ sourceAgreement.precip.unit }}</div>
                <div class="swa-agreement-card__spread">Spread: {{ sourceAgreement.precip.spread }} {{ sourceAgreement.precip.unit }}</div>
                <div class="swa-agreement-card__sources">
                  <span v-for="s in sourceAgreement.precip.sources" :key="s.name" class="swa-agreement-source">
                    <span class="swa-source-dot" :style="{ background: s.color }"></span>
                    {{ s.name }}: {{ s.value.toFixed(2) }}
                  </span>
                </div>
                <div class="swa-agreement-card__verdict">{{ sourceAgreement.precip.agreement === 'good' ? '✓ Sources agree' : sourceAgreement.precip.agreement === 'moderate' ? '⚠ Moderate spread' : '✗ Large disagreement' }}</div>
              </div>
              <div v-if="sourceAgreement.wind" class="swa-agreement-card" :class="'swa-agreement--' + sourceAgreement.wind.agreement">
                <div class="swa-agreement-card__title">Max Wind Speed</div>
                <div class="swa-agreement-card__range">{{ sourceAgreement.wind.min }} – {{ sourceAgreement.wind.max }} {{ sourceAgreement.wind.unit }}</div>
                <div class="swa-agreement-card__sources">
                  <span v-for="s in sourceAgreement.wind.sources" :key="s.name" class="swa-agreement-source">
                    <span class="swa-source-dot" :style="{ background: s.color }"></span>
                    {{ s.name }}: {{ s.value.toFixed(1) }}
                  </span>
                </div>
                <div class="swa-agreement-card__verdict">{{ sourceAgreement.wind.agreement === 'good' ? '✓ Sources agree' : sourceAgreement.wind.agreement === 'moderate' ? '⚠ Moderate spread' : '✗ Large disagreement' }}</div>
              </div>
              <div v-if="sourceAgreement.gust" class="swa-agreement-card" :class="'swa-agreement--' + sourceAgreement.gust.agreement">
                <div class="swa-agreement-card__title">Max Wind Gust</div>
                <div class="swa-agreement-card__range">{{ sourceAgreement.gust.min }} – {{ sourceAgreement.gust.max }} {{ sourceAgreement.gust.unit }}</div>
                <div class="swa-agreement-card__sources">
                  <span v-for="s in sourceAgreement.gust.sources" :key="s.name" class="swa-agreement-source">
                    <span class="swa-source-dot" :style="{ background: s.color }"></span>
                    {{ s.name }}: {{ s.value.toFixed(1) }}
                  </span>
                </div>
                <div class="swa-agreement-card__verdict">{{ sourceAgreement.gust.agreement === 'good' ? '✓ Sources agree' : sourceAgreement.gust.agreement === 'moderate' ? '⚠ Moderate spread' : '✗ Large disagreement' }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Day-by-Day Detail -->
        <div class="swa-section">
          <h3 class="swa-section__title"><i class="bi bi-calendar-event"></i> Daily Breakdown</h3>
          <div v-for="day in dailyData" :key="day.datetime" class="swa-day-card">
            <div class="swa-day-card__header" :class="dayRiskClass(day)">
              <span class="swa-day-card__date">{{ day.datetime }}</span>
              <span v-if="day.severerisk" class="swa-day-card__badge">
                <i class="bi bi-exclamation-triangle-fill"></i> Severe Risk {{ day.severerisk }}%
              </span>
              <span class="swa-day-card__desc">{{ day.description || day.conditions }}</span>
            </div>
            <div class="swa-day-card__grid">
              <div class="swa-day-stat">
                <span class="swa-day-stat__label">Precip</span>
                <span class="swa-day-stat__val">{{ formatVal(day.precip, 'precip') }}</span>
              </div>
              <div class="swa-day-stat">
                <span class="swa-day-stat__label">Precip Remote</span>
                <span class="swa-day-stat__val">{{ formatVal(day.precipremote, 'precip') }}</span>
              </div>
              <div class="swa-day-stat">
                <span class="swa-day-stat__label">Reflectivity</span>
                <span class="swa-day-stat__val">{{ formatVal(day.reflectivity, 'dbz') }}</span>
              </div>
              <div class="swa-day-stat">
                <span class="swa-day-stat__label">Wind Gust</span>
                <span class="swa-day-stat__val">{{ formatVal(day.windgust, 'windgust') }}</span>
              </div>
              <div class="swa-day-stat">
                <span class="swa-day-stat__label">Wind Speed</span>
                <span class="swa-day-stat__val">{{ formatVal(day.windspeed, 'windspeed') }}</span>
              </div>
              <div class="swa-day-stat">
                <span class="swa-day-stat__label">Lightning</span>
                <span class="swa-day-stat__val">{{ formatVal(day.lightningrisk, 'pct') }}</span>
              </div>
              <div class="swa-day-stat">
                <span class="swa-day-stat__label">Lightning Density</span>
                <span class="swa-day-stat__val">{{ formatVal(day.lightningdensity, 'density') }}</span>
              </div>
              <div class="swa-day-stat">
                <span class="swa-day-stat__label">Hail</span>
                <span class="swa-day-stat__val">{{ formatVal(day.hailrisk, 'pct') }}</span>
              </div>
              <div class="swa-day-stat">
                <span class="swa-day-stat__label">Hail Size</span>
                <span class="swa-day-stat__val">{{ formatVal(day.hailsize, 'hailsize') }}</span>
              </div>
              <div class="swa-day-stat">
                <span class="swa-day-stat__label">Severe Risk</span>
                <span class="swa-day-stat__val">{{ formatVal(day.severerisk, 'pct') }}</span>
              </div>
              <div class="swa-day-stat">
                <span class="swa-day-stat__label">CAPE</span>
                <span class="swa-day-stat__val">{{ formatVal(day.cape, 'cape') }}</span>
              </div>
              <div class="swa-day-stat">
                <span class="swa-day-stat__label">CIN</span>
                <span class="swa-day-stat__val">{{ formatVal(day.cin, 'cin') }}</span>
              </div>
              <div class="swa-day-stat">
                <span class="swa-day-stat__label">Helicity</span>
                <span class="swa-day-stat__val">{{ formatVal(day.helicity, 'helicity') }}</span>
              </div>
              <div class="swa-day-stat">
                <span class="swa-day-stat__label">Snow Depth</span>
                <span class="swa-day-stat__val">{{ formatVal(day.snowdepth, 'precip') }}</span>
              </div>
              <div class="swa-day-stat">
                <span class="swa-day-stat__label">Pressure</span>
                <span class="swa-day-stat__val">{{ formatVal(day.pressure, 'pressure') }}</span>
              </div>
              <div class="swa-day-stat" v-if="day.preciptype && day.preciptype.length">
                <span class="swa-day-stat__label">Precip Types</span>
                <span class="swa-day-stat__val">{{ day.preciptype.join(', ') }}</span>
              </div>
            </div>

            <!-- Hourly mini-table for this day -->
            <details v-if="day.hours && day.hours.length" class="swa-day-card__hourly">
              <summary>Hourly detail ({{ day.hours.length }} hours)</summary>
              <div class="swa-hourly-table-wrap">
                <table class="swa-table swa-table--compact">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Precip</th>
                      <th>Precip Remote</th>
                      <th>Reflect.</th>
                      <th>Wind</th>
                      <th>Gust</th>
                      <th>Lightning</th>
                      <th>L.Density</th>
                      <th>Hail</th>
                      <th>Hail Size</th>
                      <th>Severe</th>
                      <th>CAPE</th>
                      <th>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="h in day.hours" :key="h.datetime" :class="hourRiskClass(h)">
                      <td>{{ h.datetime }}</td>
                      <td>{{ formatVal(h.precip, 'precip') }}</td>
                      <td>{{ formatVal(h.precipremote, 'precip') }}</td>
                      <td>{{ formatVal(h.reflectivity, 'dbz') }}</td>
                      <td>{{ formatVal(h.windspeed, 'windspeed') }}</td>
                      <td>{{ formatVal(h.windgust, 'windgust') }}</td>
                      <td>{{ formatVal(h.lightningrisk, 'pct') }}</td>
                      <td>{{ formatVal(h.lightningdensity, 'density') }}</td>
                      <td>{{ formatVal(h.hailrisk, 'pct') }}</td>
                      <td>{{ formatVal(h.hailsize, 'hailsize') }}</td>
                      <td>{{ formatVal(h.severerisk, 'pct') }}</td>
                      <td>{{ formatVal(h.cape, 'cape') }}</td>
                      <td class="swa-table__src">{{ h.source || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </details>
          </div>
        </div>

        <!-- Storm Events -->
        <div v-if="events && events.length" class="swa-section">
          <h3 class="swa-section__title"><i class="bi bi-lightning-fill"></i> Storm Events ({{ events.length }})</h3>
          <div class="swa-events-table-wrap">
            <table class="swa-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Type</th>
                  <th>Location</th>
                  <th>Distance</th>
                  <th>Value</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(evt, idx) in events" :key="idx" :class="'swa-event-row--' + evt.type">
                  <td class="swa-table__nowrap">{{ evt.datetime }}</td>
                  <td><span class="swa-event-badge" :class="'swa-event-badge--' + evt.type">{{ evt.type }}</span></td>
                  <td class="swa-table__nowrap">{{ evt.latitude?.toFixed(3) }}, {{ evt.longitude?.toFixed(3) }}</td>
                  <td>{{ evt.distance != null ? evt.distance.toFixed(1) + ' ' + distUnit : '—' }}</td>
                  <td>{{ formatEventValue(evt) }}</td>
                  <td class="swa-table__note">{{ evt.desc || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Map -->
        <div class="swa-section">
          <h3 class="swa-section__title"><i class="bi bi-map"></i> Severe Weather Map</h3>
          <div class="swa-map-controls">
            <label class="swa-label">Base:</label>
            <select class="swa-input swa-input--select" v-model="mapLayer">
              <option value="none">None (markers only)</option>
              <option value="precipcomposite">Precip Composite</option>
              <option value="preciprate">Precip Rate</option>
              <option value="hailsize">Hail Size</option>
              <option value="hail">Hail</option>
              <option value="wind">Wind</option>
              <option value="temp">Temperature</option>
            </select>
            <label class="swa-label swa-label--overlay"><input type="checkbox" v-model="showLightningOverlay" /> Lightning</label>
            <label class="swa-label swa-label--overlay"><input type="checkbox" v-model="showHailOverlay" /> Hail</label>
            <label class="swa-label swa-label--overlay"><input type="checkbox" v-model="showEventMarkers" /> Events</label>
            <label class="swa-label swa-label--overlay"><input type="checkbox" v-model="showStationMarkers" /> Stations</label>
            <label class="swa-label swa-label--overlay"><input type="checkbox" v-model="showDailyObsMarkers" /> Daily Obs</label>
            <label class="swa-label swa-label--overlay"><input type="checkbox" v-model="showCitiesOverlay" /> Cities</label>
            <div class="swa-map-time-controls">
              <button class="swa-btn swa-btn--icon" @click="stepMapTime(-1)" title="Back 10 min"><i class="bi bi-chevron-left"></i></button>
              <span class="swa-map-time-label">{{ currentMapTimeLabel }}</span>
              <button class="swa-btn swa-btn--icon" @click="stepMapTime(1)" title="Forward 10 min"><i class="bi bi-chevron-right"></i></button>
              <button class="swa-btn swa-btn--icon" @click="togglePlay" :title="isPlaying ? 'Pause' : 'Play'">
                <i class="bi" :class="isPlaying ? 'bi-pause-fill' : 'bi-play-fill'"></i>
              </button>
            </div>
            <div class="swa-map-slider-wrap">
              <input type="range" class="swa-map-slider" :min="0" :max="mapTimeSlots.length - 1" v-model.number="mapTimeIndex" />
            </div>
          </div>
          <div ref="mapContainer" class="swa-map"></div>
        </div>

        <!-- API Calls -->
        <div v-if="apiCalls.length" class="swa-section">
          <h3 class="swa-section__title"><i class="bi bi-terminal"></i> API Calls ({{ apiCalls.length }})</h3>
          <div class="swa-api-calls">
            <div v-for="(call, idx) in apiCalls" :key="idx" class="swa-api-call">
              <div class="swa-api-call__label">{{ call.label }}</div>
              <div class="swa-api-call__url-row">
                <code class="swa-api-call__url">{{ call.url }}</code>
                <button class="swa-btn swa-btn--icon swa-api-call__copy" @click="copyApiUrl(call.url)" title="Copy URL">
                  <i class="bi bi-clipboard"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { VcGlobal } from '/src/visualcrossing-core/vcglobal_conn.js';
import { getServerRoot } from '/src/components/weatherexceptions/apiConfig.js';
import { getAlertByType, getAlertClass, getAlertIcon } from '/src/data/weatherAlerts.js';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { patchLeaflet } from '/src/utils/leaflet-patches.js';
import '/src/weather-map/WxTileLayer.js';
patchLeaflet();

const SEVERE_ELEMENTS = '+precipremote,+reflectivity,+reflectivity2,+severerisk,+lightningrisk,+hailrisk,+lightningdensity,+hailsize,+hailprobability,+severehailindex,+cape,+cin,+helicity,+snowdepth,+pressure,+frozenprecippercent,+windspeedmax,+windspeedmin,+windgust';

const TILE_SERVER = 'https://maps.visualcrossing.com';

const STORAGE_KEY = 'swa_prefs';

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function savePrefs(obj) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(obj)); } catch { /* ignore */ }
}

function formatDateISO(date) {
  return date.toISOString().split('T')[0];
}

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
}

export default {
  name: 'SevereWeatherAnalyzer',

  data() {
    const today = formatDateISO(new Date());
    const yesterday = formatDateISO(daysAgo(1));
    const prefs = loadPrefs();
    return {
      // Session
      sessionId: null,
      sessionState: null,

      // Controls
      locationText: prefs?.locationText || '',
      location: prefs?.location || null,
      isGeocoding: false,
      startDate: prefs?.startDate || yesterday,
      endDate: prefs?.endDate || today,
      unitGroup: prefs?.unitGroup || 'us',
      maxDistanceMiles: prefs?.maxDistanceMiles || 15,

      // State
      isLoading: false,
      error: null,

      // API results
      timelineData: null,    // main timeline (days+hours+alerts)
      hourlyData: null,      // separate hourly-only call if needed
      stationMinutelyData: {},  // { stationId: timelineResponse }
      isLoadingStations: false,
      era5Data: null,  // ERA5 reanalysis comparison
      v1ForecastData: null,  // V1 (without usev2forecast) comparison
      dailyObsData: null,  // Daily-only observation stations (usedaily,useobs)
      dailyObsStationData: {},  // { stationId: timelineResponse } per-station daily data
      isLoadingDailyObs: false,
      apiCalls: [],  // list of { label, url } for the API calls section

      // Map
      map: null,
      wxBaseLayer: null,
      wxLightningLayer: null,
      wxHailLayer: null,
      mapLayer: 'precipcomposite',
      mapTimeIndex: 0,
      locationMarker: null,
      stationMarkerGroup: null,
      dailyObsMarkerGroup: null,
      eventMarkerGroup: null,
      showLightningOverlay: true,
      showHailOverlay: true,
      showEventMarkers: true,
      showCitiesOverlay: false,
      showStationMarkers: true,
      showDailyObsMarkers: true,
      citiesLayer: null,
      isPlaying: false,
      playTimer: null,
    };
  },

  computed: {
    hasSession() {
      return !!this.weatherApiKey;
    },
    weatherApiKey() {
      if (this.sessionState?.apiKey) return this.sessionState.apiKey;
      if (window.vcApiKey) return window.vcApiKey;
      return null;
    },
    serverRoot() {
      return getServerRoot();
    },
    canAnalyze() {
      return this.location && this.startDate && this.endDate;
    },

    maxDistanceMeters() {
      return Math.round(this.maxDistanceMiles * 1609.344);
    },

    distanceOptions() {
      const isMetric = this.unitGroup === 'metric' || this.unitGroup === 'base';
      if (isMetric) {
        return [
          { value: 6, label: '10 km' },
          { value: 10, label: '16 km' },
          { value: 15, label: '25 km' },
          { value: 25, label: '40 km' },
          { value: 50, label: '80 km' },
        ];
      }
      return [
        { value: 6, label: '6 mi' },
        { value: 10, label: '10 mi' },
        { value: 15, label: '15 mi' },
        { value: 25, label: '25 mi' },
        { value: 50, label: '50 mi' },
      ];
    },

    hasResults() {
      return this.timelineData && this.timelineData.days && this.timelineData.days.length > 0;
    },
    distUnit() {
      return this.unitGroup === 'us' || this.unitGroup === 'uk' ? 'mi' : 'km';
    },

    // Parse daily data from timeline
    dailyData() {
      if (!this.timelineData?.days) return [];
      return this.timelineData.days;
    },

    // Alerts from timeline
    alerts() {
      if (!this.timelineData?.alerts) return [];
      return this.timelineData.alerts;
    },

    // Events from timeline (hail, tornado, wind, earthquake)
    events() {
      if (!this.timelineData?.days) return [];
      const all = [];
      for (const d of this.timelineData.days) {
        if (d.events) {
          for (const e of d.events) all.push(e);
        }
      }
      return all.sort((a, b) => (a.datetimeEpoch || 0) - (b.datetimeEpoch || 0));
    },

    // Stations from timeline
    stations() {
      if (!this.timelineData?.stations) return [];
      const toMiles = this.unitGroup === 'us' || this.unitGroup === 'uk';
      return Object.values(this.timelineData.stations).map(s => {
        let dist = s.distance;
        if (dist != null) {
          dist = toMiles ? (dist / 1609.344) : (dist / 1000);
        }
        return {
          id: s.id,
          name: s.name,
          latitude: s.latitude,
          longitude: s.longitude,
          distance: dist != null ? dist.toFixed(1) : '?',
        };
      }).sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance));
    },

    // KPIs
    kpis() {
      const days = this.dailyData;
      if (!days.length) return {};

      let maxPrecip = 0, maxWindGust = 0, maxLightningRisk = 0, maxHailRisk = 0;
      let maxSevereRisk = 0, maxReflectivity = 0, maxCAPE = 0;
      let maxHailSize = 0, maxLightningDensity = 0;

      for (const d of days) {
        maxPrecip = Math.max(maxPrecip, d.precip || 0, d.precipremote || 0);
        maxWindGust = Math.max(maxWindGust, d.windgust || 0);
        maxLightningRisk = Math.max(maxLightningRisk, d.lightningrisk || 0);
        maxHailRisk = Math.max(maxHailRisk, d.hailrisk || 0);
        maxSevereRisk = Math.max(maxSevereRisk, d.severerisk || 0);
        maxReflectivity = Math.max(maxReflectivity, d.reflectivity || 0);
        maxCAPE = Math.max(maxCAPE, d.cape || 0);
        maxHailSize = Math.max(maxHailSize, d.hailsize || 0);
        maxLightningDensity = Math.max(maxLightningDensity, d.lightningdensity || 0);

        // Also check hourly for finer resolution
        if (d.hours) {
          for (const h of d.hours) {
            maxPrecip = Math.max(maxPrecip, h.precip || 0, h.precipremote || 0);
            maxWindGust = Math.max(maxWindGust, h.windgust || 0);
            maxLightningRisk = Math.max(maxLightningRisk, h.lightningrisk || 0);
            maxHailRisk = Math.max(maxHailRisk, h.hailrisk || 0);
            maxSevereRisk = Math.max(maxSevereRisk, h.severerisk || 0);
            maxReflectivity = Math.max(maxReflectivity, h.reflectivity || 0);
            maxCAPE = Math.max(maxCAPE, h.cape || 0);
            maxHailSize = Math.max(maxHailSize, h.hailsize || 0);
            maxLightningDensity = Math.max(maxLightningDensity, h.lightningdensity || 0);
          }
        }
      }

      return { maxPrecip, maxWindGust, maxLightningRisk, maxHailRisk, maxSevereRisk, maxReflectivity, maxCAPE, maxHailSize, maxLightningDensity };
    },

    severeOccurred() {
      const k = this.kpis;
      return (k.maxSevereRisk >= 30) || (k.maxHailRisk >= 30) || (k.maxLightningRisk >= 50) ||
        (k.maxReflectivity >= 50) || (k.maxWindGust >= (this.unitGroup === 'us' ? 58 : 93)) ||
        (this.alerts && this.alerts.length > 0);
    },

    severityScore() {
      const k = this.kpis;
      const evts = this.events;
      if (!k.maxSevereRisk && !k.maxWindGust && !k.maxHailSize && !k.maxLightningDensity && !evts.length) return 0;

      // Event type check — tornado nearby is automatic 10
      const hasTornado = evts.some(e => e.type === 'tornado');
      if (hasTornado) return 10;

      let score = 0;
      // Base weather elements
      score += Math.min(2.5, (k.maxSevereRisk || 0) / 30);
      score += Math.min(1.5, (k.maxLightningRisk || 0) / 50);
      score += Math.min(1.5, (k.maxHailRisk || 0) / 40);
      score += Math.min(1, (k.maxReflectivity || 0) / 50);
      score += Math.min(1, (k.maxWindGust || 0) / (this.unitGroup === 'us' ? 70 : 110));
      // Hailsize: 25mm (~1in) is severe threshold
      const hailThresh = this.unitGroup === 'us' ? 1 : 25;
      score += Math.min(1.5, (k.maxHailSize || 0) / hailThresh);
      // Lightning density
      score += Math.min(1, (k.maxLightningDensity || 0) / 2);
      // Events by type — hail event snaps to at least 8, wind event adds big boost
      const hasHailEvt = evts.some(e => e.type === 'hail');
      const windEvts = evts.filter(e => e.type === 'wind').length;
      if (hasHailEvt) score = Math.max(score, 8);
      score += Math.min(2, windEvts * 1);
      return Math.min(10, Math.round(score));
    },

    kpiSeverityClass() {
      const s = this.severityScore;
      if (s >= 7) return 'swa-kpi--danger';
      if (s >= 4) return 'swa-kpi--warning';
      return 'swa-kpi--safe';
    },

    // Station vs Remote comparison rows
    comparisonRows() {
      const days = this.dailyData;
      if (!days.length) return [];

      let stationPrecip = 0, remotePrecip = 0;
      let stationWind = 0, stationGust = 0;
      let totalReflect = 0, dayCount = days.length;

      for (const d of days) {
        stationPrecip += d.precip || 0;
        remotePrecip += d.precipremote || 0;
        stationWind = Math.max(stationWind, d.windspeed || 0);
        stationGust = Math.max(stationGust, d.windgust || 0);
        totalReflect = Math.max(totalReflect, d.reflectivity || 0);
        if (d.hours) {
          for (const h of d.hours) {
            totalReflect = Math.max(totalReflect, h.reflectivity || 0);
          }
        }
      }

      // ERA5 aggregation — preserve null when all values are missing
      let era5Precip = null, era5Wind = null, era5Gust = null, era5Reflect = null;
      if (this.era5Data?.days) {
        for (const d of this.era5Data.days) {
          if (d.precip != null) era5Precip = (era5Precip || 0) + d.precip;
          if (d.windspeed != null) era5Wind = Math.max(era5Wind || 0, d.windspeed);
          if (d.windgust != null) era5Gust = Math.max(era5Gust || 0, d.windgust);
          if (d.reflectivity != null) era5Reflect = Math.max(era5Reflect || 0, d.reflectivity);
          if (d.hours) {
            for (const h of d.hours) {
              if (h.reflectivity != null) era5Reflect = Math.max(era5Reflect || 0, h.reflectivity);
            }
          }
        }
      }

      // V1 Forecast aggregation (precipremote + reflectivity, without usev2forecast)
      let v1Precip = null, v1Reflect = null;
      if (this.v1ForecastData?.days) {
        for (const d of this.v1ForecastData.days) {
          if (d.precipremote != null) v1Precip = (v1Precip || 0) + d.precipremote;
          if (d.reflectivity != null) v1Reflect = Math.max(v1Reflect || 0, d.reflectivity);
          if (d.hours) {
            for (const h of d.hours) {
              if (h.reflectivity != null) v1Reflect = Math.max(v1Reflect || 0, h.reflectivity);
            }
          }
        }
      }

      // Daily Obs aggregation — uses next-day precip (8am-8am offset)
      let dailyObsPrecip = null, dailyObsWind = null, dailyObsGust = null, dailyObsTempMax = null, dailyObsTempMin = null;
      if (this.dailyObsData?.days) {
        const obsDays = this.dailyObsData.days;
        const analysisDateSet = new Set(days.map(d => d.datetime));
        dailyObsTempMax = -Infinity; dailyObsTempMin = Infinity;
        // For each analysis day, use the NEXT day's precip from daily obs (8am-8am cycle)
        for (let i = 0; i < obsDays.length; i++) {
          const od = obsDays[i];
          if (analysisDateSet.has(od.datetime)) {
            // Temp/wind use same-day values
            if (od.windspeed != null) dailyObsWind = Math.max(dailyObsWind || 0, od.windspeed);
            if (od.windgust != null) dailyObsGust = Math.max(dailyObsGust || 0, od.windgust);
            if (od.tempmax != null) dailyObsTempMax = Math.max(dailyObsTempMax, od.tempmax);
            if (od.tempmin != null) dailyObsTempMin = Math.min(dailyObsTempMin, od.tempmin);
            // Precip: use next day's value for this analysis day
            const nextDay = obsDays[i + 1];
            const precipVal = nextDay ? nextDay.precip : od.precip;
            if (precipVal != null) dailyObsPrecip = (dailyObsPrecip || 0) + precipVal;
          }
        }
        if (dailyObsTempMax === -Infinity) dailyObsTempMax = null;
        if (dailyObsTempMin === Infinity) dailyObsTempMin = null;
      }

      const precipDelta = remotePrecip - stationPrecip;
      const precipUnit = this.unitGroup === 'us' ? 'in' : 'mm';
      const windUnit = this.unitGroup === 'us' ? 'mph' : this.unitGroup === 'metric' ? 'km/h' : 'm/s';
      const fmt = (v, decimals, unit) => v != null ? `${v.toFixed(decimals)} ${unit}` : '—';

      return [
        {
          element: 'precip', label: 'Total Precipitation',
          station: `${stationPrecip.toFixed(2)} ${precipUnit}`,
          remote: `${remotePrecip.toFixed(2)} ${precipUnit}`,
          dailyObs: fmt(dailyObsPrecip, 2, precipUnit),
          era5: fmt(era5Precip, 2, precipUnit),
          v1forecast: fmt(v1Precip, 2, precipUnit),
          delta: `${precipDelta >= 0 ? '+' : ''}${precipDelta.toFixed(2)} ${precipUnit}`,
          deltaClass: Math.abs(precipDelta) > stationPrecip * 0.3 ? 'swa-delta--warn' : '',
          note: Math.abs(precipDelta) > stationPrecip * 0.5 ? 'Large discrepancy – station may have missed event or gauge undercatch' : 'Within normal range'
        },
        {
          element: 'reflectivity', label: 'Max Reflectivity (Radar)',
          station: '—',
          remote: `${totalReflect.toFixed(0)} dBZ`,
          dailyObs: '—',
          era5: fmt(era5Reflect, 0, 'dBZ'),
          v1forecast: fmt(v1Reflect, 0, 'dBZ'),
          delta: '—',
          deltaClass: '',
          note: totalReflect >= 50 ? 'Heavy precipitation / possible severe' : totalReflect >= 35 ? 'Moderate precipitation' : 'Light or no precip'
        },
        {
          element: 'wind', label: 'Max Wind Speed',
          station: `${stationWind.toFixed(1)} ${windUnit}`,
          remote: '—',
          dailyObs: fmt(dailyObsWind, 1, windUnit),
          era5: fmt(era5Wind, 1, windUnit),
          v1forecast: '—',
          delta: '—',
          deltaClass: '',
          note: ''
        },
        {
          element: 'windgust', label: 'Max Wind Gust',
          station: `${stationGust.toFixed(1)} ${windUnit}`,
          remote: '—',
          dailyObs: fmt(dailyObsGust, 1, windUnit),
          era5: fmt(era5Gust, 1, windUnit),
          v1forecast: '—',
          delta: '—',
          deltaClass: '',
          note: stationGust >= (this.unitGroup === 'us' ? 58 : 93) ? 'Severe-criteria gust' : ''
        },
      ];
    },

    // Summaries from individual station 15-minute data
    stationSummaries() {
      const summaries = [];
      for (const stn of this.stations) {
        const data = this.stationMinutelyData[stn.id];
        if (!data || !data.days) {
          summaries.push({
            id: stn.id,
            name: stn.name || stn.id,
            distance: stn.distance,
            latitude: stn.latitude,
            longitude: stn.longitude,
            totalPrecip: null,
            maxWindSpeed: null,
            maxWindGust: null,
            minuteCount: 0,
            loaded: false,
          });
          continue;
        }

        let totalPrecip = 0;
        let maxWindSpeed = 0;
        let maxWindGust = 0;
        let minuteCount = 0;

        for (const day of data.days) {
          if (day.hours && day.hours.length) {
            for (const h of day.hours) {
              // Prefer minutely data within each hour (15-min intervals)
              if (h.minutes && h.minutes.length) {
                for (const m of h.minutes) {
                  totalPrecip += m.precip || 0;
                  maxWindSpeed = Math.max(maxWindSpeed, m.windspeed || 0);
                  maxWindGust = Math.max(maxWindGust, m.windgust || 0);
                  minuteCount++;
                }
              } else {
                totalPrecip += h.precip || 0;
                maxWindSpeed = Math.max(maxWindSpeed, h.windspeed || 0);
                maxWindGust = Math.max(maxWindGust, h.windgust || 0);
                minuteCount++;
              }
            }
          } else {
            totalPrecip += day.precip || 0;
            maxWindSpeed = Math.max(maxWindSpeed, day.windspeed || 0);
            maxWindGust = Math.max(maxWindGust, day.windgust || 0);
            minuteCount++;
          }
        }

        summaries.push({
          id: stn.id,
          name: stn.name || stn.id,
          distance: stn.distance,
          latitude: stn.latitude,
          longitude: stn.longitude,
          totalPrecip,
          maxWindSpeed,
          maxWindGust,
          minuteCount,
          loaded: true,
        });
      }
      return summaries;
    },

    // Daily observation stations — individual station breakdown from stn: requests
    dailyObsStations() {
      if (!this.dailyObsData?.stations) return [];
      const stationsMap = this.dailyObsData.stations;
      const analysisDates = new Set(this.dailyData.map(d => d.datetime));
      const toMiles = this.unitGroup === 'us' || this.unitGroup === 'uk';

      return Object.entries(stationsMap).map(([id, stn]) => {
        let dist = stn.distance;
        if (dist != null) {
          dist = toMiles ? (dist / 1609.344) : (dist / 1000);
        }

        const stnData = this.dailyObsStationData[id];
        const dayReadings = [];
        let totalPrecip = 0, maxWind = 0, maxGust = 0;

        if (stnData?.days) {
          const stnDays = stnData.days;
          for (let i = 0; i < stnDays.length; i++) {
            const sd = stnDays[i];
            if (!analysisDates.has(sd.datetime)) continue;
            // Precip: use next day's value (8am-8am offset)
            const nextDay = stnDays[i + 1];
            const precip = nextDay ? (nextDay.precip || 0) : (sd.precip || 0);
            dayReadings.push({
              date: sd.datetime,
              precip,
              precipSameDay: sd.precip || 0,
              tempmax: sd.tempmax,
              tempmin: sd.tempmin,
              windspeed: sd.windspeed,
              windgust: sd.windgust,
            });
            totalPrecip += precip;
            maxWind = Math.max(maxWind, sd.windspeed || 0);
            maxGust = Math.max(maxGust, sd.windgust || 0);
          }
        }

        // Prefer lat/lon from individual stn: response (blended response often has longitude=0)
        const lat = stnData?.latitude != null ? stnData.latitude : stn.latitude;
        const lon = stnData?.longitude != null ? stnData.longitude : stn.longitude;

        return {
          id,
          name: stn.name || id,
          distance: dist != null ? dist.toFixed(1) : '?',
          latitude: lat,
          longitude: lon,
          loaded: !!stnData,
          dayReadings,
          totalPrecip,
          maxWind,
          maxGust,
        };
      });
    },

    // Source agreement summary across all sources
    sourceAgreement() {
      const rows = this.comparisonRows;
      if (!rows.length) return null;

      const precipRow = rows.find(r => r.element === 'precip');
      const windRow = rows.find(r => r.element === 'wind');
      const gustRow = rows.find(r => r.element === 'windgust');
      if (!precipRow) return null;

      const precipUnit = this.unitGroup === 'us' ? 'in' : 'mm';
      const windUnit = this.unitGroup === 'us' ? 'mph' : this.unitGroup === 'metric' ? 'km/h' : 'm/s';

      // Extract numeric values from formatted strings
      const parseNum = (s) => { const m = String(s).match(/[\d.]+/); return m ? parseFloat(m[0]) : null; };

      const precipSources = [
        { name: 'Stn Hourly', value: parseNum(precipRow.station), color: '#3b82f6' },
        { name: 'Stn Daily', value: parseNum(precipRow.dailyObs), color: '#0891b2' },
        { name: 'Radar', value: parseNum(precipRow.remote), color: '#16a34a' },
        { name: 'ERA5', value: parseNum(precipRow.era5), color: '#d97706' },
        { name: 'Radar V1', value: parseNum(precipRow.v1forecast), color: '#e11d48' },
      ].filter(s => s.value != null && !isNaN(s.value));

      const windSources = [
        { name: 'Stn Hourly', value: parseNum(windRow?.station), color: '#3b82f6' },
        { name: 'Stn Daily', value: parseNum(windRow?.dailyObs), color: '#0891b2' },
        { name: 'ERA5', value: parseNum(windRow?.era5), color: '#d97706' },
      ].filter(s => s.value != null && !isNaN(s.value));

      const gustSources = [
        { name: 'Stn Hourly', value: parseNum(gustRow?.station), color: '#3b82f6' },
        { name: 'Stn Daily', value: parseNum(gustRow?.dailyObs), color: '#0891b2' },
        { name: 'ERA5', value: parseNum(gustRow?.era5), color: '#d97706' },
      ].filter(s => s.value != null && !isNaN(s.value));

      const summarize = (sources, unit, decimals) => {
        if (sources.length < 2) return null;
        const vals = sources.map(s => s.value);
        const min = Math.min(...vals);
        const max = Math.max(...vals);
        const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
        const spread = max - min;
        const agreement = spread <= avg * 0.3 ? 'good' : spread <= avg * 0.6 ? 'moderate' : 'poor';
        return {
          sources,
          min: min.toFixed(decimals),
          max: max.toFixed(decimals),
          avg: avg.toFixed(decimals),
          spread: spread.toFixed(decimals),
          unit,
          agreement,
        };
      };

      return {
        precip: summarize(precipSources, precipUnit, 2),
        wind: summarize(windSources, windUnit, 1),
        gust: summarize(gustSources, windUnit, 1),
      };
    },

    // Build 10-minute time slots across the entire date range
    mapTimeSlots() {
      const slots = [];
      if (!this.dailyData.length) {
        slots.push({ epoch: Math.floor(Date.now() / 1000), date: new Date() });
        return slots;
      }
      // Start from first day epoch, end at last day epoch + 24h
      const firstDay = this.dailyData[0];
      const lastDay = this.dailyData[this.dailyData.length - 1];
      let startEpoch = firstDay.datetimeEpoch;
      let endEpoch = lastDay.datetimeEpoch + 86400; // +24h
      const STEP = 600; // 10 minutes in seconds
      for (let t = startEpoch; t < endEpoch; t += STEP) {
        slots.push({ epoch: t, date: new Date(t * 1000) });
      }
      return slots;
    },

    currentMapTimeLabel() {
      const slot = this.mapTimeSlots[this.mapTimeIndex];
      if (!slot) return '';
      const d = slot.date;
      const pad = n => String(n).padStart(2, '0');
      return `${d.getUTCFullYear()}-${pad(d.getUTCMonth()+1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
    },
  },

  watch: {
    mapLayer() { this.updateBaseLayer(); },
    mapTimeIndex() { this.updateMapDateTime(); },
    showLightningOverlay(v) { this.toggleOverlayLayer('lightning', v); },
    showHailOverlay(v) { this.toggleOverlayLayer('hail', v); },
    showEventMarkers() { this.updateEventMarkers(); },
    showStationMarkers() { this.updateStationMarkers(); },
    showDailyObsMarkers() { this.updateDailyObsMarkers(); },
    stationSummaries: { deep: true, handler() { this.updateStationMarkers(); } },
    dailyObsStations: { deep: true, handler() { this.updateDailyObsMarkers(); } },
    showCitiesOverlay(v) { this.toggleCitiesLayer(v); },
    locationText() { this.persistPrefs(); },
    location: { deep: true, handler() { this.persistPrefs(); } },
    startDate(val) {
      const minDate = '2026-02-01';
      if (val < minDate) { this.startDate = minDate; return; }
      if (val > this.endDate) { this.endDate = val; }
      const start = new Date(val), end = new Date(this.endDate);
      if ((end - start) / 86400000 > 3) { this.endDate = formatDateISO(new Date(start.getTime() + 3 * 86400000)); }
      this.persistPrefs();
    },
    endDate(val) {
      if (val < this.startDate) { this.endDate = this.startDate; return; }
      const start = new Date(this.startDate), end = new Date(val);
      if ((end - start) / 86400000 > 3) { this.endDate = formatDateISO(new Date(start.getTime() + 3 * 86400000)); return; }
      this.persistPrefs();
    },
    unitGroup() { this.persistPrefs(); },
    maxDistanceMiles() { this.persistPrefs(); },
  },

  mounted() {
    // Session handling
    const vcCommunicator = window.vcCommunicator;
    if (vcCommunicator) {
      this.sessionId = vcCommunicator.sessionId;
      this.sessionState = vcCommunicator.sessionState;

      vcCommunicator.addHandler('createsession', (sessionId, sessionState) => {
        this.sessionId = sessionId;
        this.sessionState = sessionState;
      });
      vcCommunicator.addHandler('terminatesession', () => {
        this.sessionId = null;
        this.sessionState = null;
      });
    }
  },

  beforeUnmount() {
    if (this.playTimer) clearInterval(this.playTimer);
    if (this.citiesLayer && this.map) this.map.removeLayer(this.citiesLayer);
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },

  methods: {
    showSignIn() {
      window.dialogManager?.showSignIn();
    },

    persistPrefs() {
      savePrefs({
        locationText: this.locationText,
        location: this.location,
        startDate: this.startDate,
        endDate: this.endDate,
        unitGroup: this.unitGroup,
        maxDistanceMiles: this.maxDistanceMiles,
      });
    },

    // ---- Location ----
    async geocodeLocation() {
      if (!this.locationText.trim()) return;
      this.isGeocoding = true;
      try {
        const vcCommunicator = window.vcCommunicator;
        if (vcCommunicator && vcCommunicator.geocode) {
          const result = await vcCommunicator.geocode(this.locationText);
          if (result && result.length > 0) {
            this.location = {
              lat: result[0].latitude,
              lon: result[0].longitude,
              name: result[0].resolvedAddress || this.locationText
            };
            return;
          }
        }
        // Fallback: try direct API geocoding
        const url = `${this.serverRoot}/VisualCrossingWebServices/rest/services/timeline/${encodeURIComponent(this.locationText)}?key=${this.weatherApiKey}&include=current&elements=temp`;
        const resp = await fetch(url); 
        if (resp.ok) {
          const data = await resp.json();
          if (data.latitude != null) {
            this.location = { lat: data.latitude, lon: data.longitude, name: data.resolvedAddress || this.locationText };
            return;
          }
        }
        // Final fallback: lat,lon parse
        const match = this.locationText.match(/^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/);
        if (match) {
          this.location = { lat: parseFloat(match[1]), lon: parseFloat(match[2]), name: this.locationText };
        } else {
          this.error = 'Could not geocode location. Try coordinates (lat,lon).';
        }
      } catch (err) {
        console.error('Geocoding error:', err);
        this.error = 'Geocoding failed: ' + err.message;
      } finally {
        this.isGeocoding = false;
      }
    },

    useCurrentLocation() {
      if (!navigator.geolocation) return;
      this.isGeocoding = true;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.location = { lat: pos.coords.latitude, lon: pos.coords.longitude, name: 'Current Location' };
          this.locationText = `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`;
          this.isGeocoding = false;
        },
        () => { this.isGeocoding = false; }
      );
    },

    // ---- Date helpers ----
    setDateRange(range) {
      const today = new Date();
      switch (range) {
        case 'yesterday': {
          const y = daysAgo(1);
          this.startDate = formatDateISO(y);
          this.endDate = formatDateISO(y);
          break;
        }
        case 'today':
          this.startDate = formatDateISO(today);
          this.endDate = formatDateISO(today);
          break;
        case '3days':
          this.startDate = formatDateISO(daysAgo(3));
          this.endDate = formatDateISO(today);
          break;
        case 'week':
          this.startDate = formatDateISO(daysAgo(7));
          this.endDate = formatDateISO(today);
          break;
      }
    },

    // ---- Analysis ----
    async analyze() {
      if (!this.canAnalyze) return;
      this.isLoading = true;
      this.error = null;
      this.timelineData = null;
      this.stationMinutelyData = {};
      this.isLoadingStations = false;
      this.era5Data = null;
      this.v1ForecastData = null;
      this.dailyObsData = null;
      this.dailyObsStationData = {};
      this.isLoadingDailyObs = false;
      this.apiCalls = [];

      try {
        const loc = `${this.location.lat},${this.location.lon}`;
        // Main timeline call: days + hours + alerts + stations
        const params = new URLSearchParams({
          key: this.weatherApiKey,
          unitGroup: this.unitGroup,
          include: 'days,hours,minutes,alerts,stations,events',
          iconSet: 'icons2',
          elements: SEVERE_ELEMENTS,
          options: 'attribution,usev2forecast',
          maxDistance: this.maxDistanceMeters,
        });

        const url = `${this.serverRoot}/VisualCrossingWebServices/rest/services/timeline/${encodeURIComponent(loc)}/${this.startDate}/${this.endDate}?${params}`;
        this.apiCalls.push({ label: 'Main Timeline V2 (days+hours+minutes+alerts+stations+events)', url });

        const resp = await fetch(url, { method: 'GET', mode: 'cors', cache: 'no-cache' });
        if (!resp.ok) {
          const text = await resp.text();
          throw new Error(`API returned ${resp.status}: ${text}`);
        }
        this.timelineData = await resp.json();

        // Jump map to peak severity hour
        this.jumpToMaxSeverity();

        // Initialize map after data loaded
        this.$nextTick(() => { this.initMap(); });

        // Fetch 15-minutely data for each contributing station (non-blocking)
        this.fetchStationMinutely();

        // Fetch ERA5 reanalysis for comparison (non-blocking)
        this.fetchEra5Data(loc);

        // Fetch V1 (without usev2forecast) comparison (non-blocking)
        this.fetchV1ForecastData(loc);

        // Fetch daily-only obs stations (non-blocking)
        this.fetchDailyObsData(loc);
      } catch (err) {
        this.error = err.message;
        console.error('Analyze error:', err);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchStationMinutely() {
      if (!this.stations.length) return;
      this.isLoadingStations = true;
      this.stationMinutelyData = {};

      const elements = 'datetime,temp,windspeed,windgust,precip,stations';
      const fetches = this.stations.map(async (stn) => {
        try {
          const params = new URLSearchParams({
            key: this.weatherApiKey,
            unitGroup: this.unitGroup,
            include: 'minutes',
            elements,
          });
          const url = `${this.serverRoot}/VisualCrossingWebServices/rest/services/timeline/stn:${encodeURIComponent(stn.id)}/${this.startDate}/${this.endDate}?${params}`;
          this.apiCalls.push({ label: `Station ${stn.id} (15-min)`, url });
          const resp = await fetch(url, { method: 'GET', mode: 'cors', cache: 'no-cache' });
          if (!resp.ok) return;
          const data = await resp.json();
          this.stationMinutelyData[stn.id] = data;
        } catch (err) {
          console.warn(`Failed to fetch minutely for station ${stn.id}:`, err);
        }
      });

      await Promise.all(fetches);
      this.stationMinutelyData = { ...this.stationMinutelyData };
      this.isLoadingStations = false;
    },

    async fetchEra5Data(loc) {
      try {
        const params = new URLSearchParams({
          key: this.weatherApiKey,
          unitGroup: this.unitGroup,
          include: 'days,hours,stations',
          iconSet: 'icons2',
          elements: SEVERE_ELEMENTS,
          options: 'attribution,useremote',
          forecastDataset: 'era5core',
        });
        const url = `${this.serverRoot}/VisualCrossingWebServices/rest/services/timeline/${encodeURIComponent(loc)}/${this.startDate}/${this.endDate}?${params}`;
        this.apiCalls.push({ label: 'ERA5 Reanalysis', url });
        this.apiCalls = [...this.apiCalls];
        const resp = await fetch(url, { method: 'GET', mode: 'cors', cache: 'no-cache' });
        if (!resp.ok) return;
        this.era5Data = await resp.json();
      } catch (err) {
        console.warn('Failed to fetch ERA5 data:', err);
      }
    },

    async fetchV1ForecastData(loc) {
      try {
        const params = new URLSearchParams({
          key: this.weatherApiKey,
          unitGroup: this.unitGroup,
          include: 'days,hours,stations',
          iconSet: 'icons2',
          elements: '+precipremote,+reflectivity',
          options: 'attribution',
        });
        const url = `${this.serverRoot}/VisualCrossingWebServices/rest/services/timeline/${encodeURIComponent(loc)}/${this.startDate}/${this.endDate}?${params}`;
        this.apiCalls.push({ label: 'V1 Forecast (no usev2forecast)', url });
        this.apiCalls = [...this.apiCalls];
        const resp = await fetch(url, { method: 'GET', mode: 'cors', cache: 'no-cache' });
        if (!resp.ok) return;
        this.v1ForecastData = await resp.json();
      } catch (err) {
        console.warn('Failed to fetch v1 forecast data:', err);
      }
    },

    async fetchDailyObsData(loc) {
      this.isLoadingDailyObs = true;
      this.dailyObsStationData = {};
      try {
        // Daily stations report 8am-8am, so fetch +1 day to capture the full analysis day
        const endPlus1 = formatDateISO(new Date(new Date(this.endDate + 'T00:00:00').getTime() + 86400000));
        const params = new URLSearchParams({
          key: this.weatherApiKey,
          unitGroup: this.unitGroup,
          include: 'days,stations',
          options: 'usedaily,useobs',
          maxDistance: this.maxDistanceMeters,
          contentType: 'json',
        });
        const url = `${this.serverRoot}/VisualCrossingWebServices/rest/services/timeline/${encodeURIComponent(loc)}/${this.startDate}/${endPlus1}?${params}`;
        this.apiCalls.push({ label: 'Daily Obs Blended (usedaily,useobs)', url });
        this.apiCalls = [...this.apiCalls];
        const resp = await fetch(url, { method: 'GET', mode: 'cors', cache: 'no-cache' });
        if (!resp.ok) return;
        this.dailyObsData = await resp.json();

        // Now fetch each individual station with stn: prefix
        if (this.dailyObsData.stations) {
          const stationIds = Object.keys(this.dailyObsData.stations);
          const fetches = stationIds.map(async (stnId) => {
            try {
              const stnParams = new URLSearchParams({
                key: this.weatherApiKey,
                unitGroup: this.unitGroup,
                include: 'days',
                options: 'usedaily,useobs',
                contentType: 'json',
              });
              const stnUrl = `${this.serverRoot}/VisualCrossingWebServices/rest/services/timeline/stn:${encodeURIComponent(stnId)}/${this.startDate}/${endPlus1}?${stnParams}`;
              this.apiCalls.push({ label: `Daily Obs stn:${stnId}`, url: stnUrl });
              const stnResp = await fetch(stnUrl, { method: 'GET', mode: 'cors', cache: 'no-cache' });
              if (!stnResp.ok) return;
              const stnData = await stnResp.json();
              this.dailyObsStationData[stnId] = stnData;
            } catch (err) {
              console.warn(`Failed to fetch daily obs for station ${stnId}:`, err);
            }
          });
          await Promise.all(fetches);
          this.dailyObsStationData = { ...this.dailyObsStationData };
          this.apiCalls = [...this.apiCalls];
        }
      } catch (err) {
        console.warn('Failed to fetch daily obs data:', err);
      } finally {
        this.isLoadingDailyObs = false;
      }
    },

    // Find the hour with the highest composite severity and set mapTimeIndex to it
    jumpToMaxSeverity() {
      // Find the hour with the highest severity, then snap to the nearest 10-min slot
      let bestEpoch = 0;
      let bestScore = -1;
      for (const d of this.dailyData) {
        if (d.hours) {
          for (const h of d.hours) {
            const score = (h.severerisk || 0) * 2 + (h.lightningrisk || 0) + (h.hailrisk || 0) +
              (h.reflectivity || 0) / 10 + (h.windgust || 0) / 10 + (h.cape || 0) / 500;
            if (score > bestScore) { bestScore = score; bestEpoch = h.datetimeEpoch || 0; }
          }
        } else {
          const score = (d.severerisk || 0) * 2 + (d.lightningrisk || 0) + (d.hailrisk || 0) +
            (d.reflectivity || 0) / 10 + (d.windgust || 0) / 10 + (d.cape || 0) / 500;
          if (score > bestScore) { bestScore = score; bestEpoch = d.datetimeEpoch || 0; }
        }
      }
      // Also check events — an event trumps computed risk if present
      if (this.events.length) {
        const evtEpoch = this.events[0].datetimeEpoch;
        if (evtEpoch) bestEpoch = evtEpoch;
      }
      // Find closest 10-min slot
      const slots = this.mapTimeSlots;
      let closestIdx = 0;
      let closestDist = Infinity;
      for (let i = 0; i < slots.length; i++) {
        const dist = Math.abs(slots[i].epoch - bestEpoch);
        if (dist < closestDist) { closestDist = dist; closestIdx = i; }
      }
      this.mapTimeIndex = closestIdx;
    },

    formatEventValue(evt) {
      if (!evt) return '—';
      if (evt.type === 'hail' && evt.size != null) {
        return this.unitGroup === 'us' ? `${evt.size}" hail` : `${(evt.size * 25.4).toFixed(0)} mm hail`;
      }
      if (evt.type === 'wind' && evt.value != null) {
        return this.unitGroup === 'us' ? `${evt.value} mph` : `${(evt.value * 1.609).toFixed(0)} km/h`;
      }
      if (evt.type === 'tornado' && evt.value != null) {
        return `F${evt.value}`;
      }
      if (evt.type === 'earthquake' && evt.value != null) {
        return `M${evt.value}`;
      }
      if (evt.value != null) return String(evt.value);
      return '—';
    },

    // ---- Map ----
    initMap() {
      if (!this.$refs.mapContainer) return;
      if (this.map) {
        this.map.remove();
        this.map = null;
      }

      // Set initial dateTime from the current time slot
      const slot = this.mapTimeSlots[this.mapTimeIndex];
      const initialDateTime = slot ? slot.date : new Date();

      this.map = L.map(this.$refs.mapContainer, {
        zoomControl: true,
        dateTime: initialDateTime,
      }).setView([this.location.lat, this.location.lon], 9);

      // Base map tiles (OSM)
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 18,
        attribution: '&copy; OpenStreetMap contributors'
      }).addTo(this.map);

      // Weather base layer (precipcomposite by default)
      this.wxBaseLayer = new L.TileLayer.WxTileLayer({
        opacity: 0.65,
        level: 0,
        model: null,
        element: this.mapLayer,
        forceRefresh: true,
        key: this.weatherApiKey,
        unitGroup: this.unitGroup,
        dateTimeInterval: 10,
      });
      this.wxBaseLayer.addTo(this.map);

      // Lightning overlay (on by default)
      if (this.showLightningOverlay) {
        this.wxLightningLayer = new L.TileLayer.WxTileLayer({
          opacity: 0.7,
          level: 0,
          model: null,
          element: 'lightning',
          forceRefresh: true,
          key: this.weatherApiKey,
          unitGroup: this.unitGroup,
          dateTimeInterval: 10,
        });
        this.wxLightningLayer.addTo(this.map);
      }

      // Hail overlay (off by default)
      if (this.showHailOverlay) {
        this.wxHailLayer = new L.TileLayer.WxTileLayer({
          opacity: 0.7,
          level: 0,
          model: null,
          element: 'hail',
          forceRefresh: true,
          key: this.weatherApiKey,
          unitGroup: this.unitGroup,
          dateTimeInterval: 10,
        });
        this.wxHailLayer.addTo(this.map);
      }

      // Location marker
      this.locationMarker = L.marker([this.location.lat, this.location.lon], {
        icon: L.divIcon({
          className: 'swa-location-icon',
          html: '<i class="bi bi-geo-alt-fill"></i>',
          iconSize: [32, 32],
          iconAnchor: [16, 32],
          popupAnchor: [0, -32],
        }),
      }).addTo(this.map).bindPopup(`<strong>${this.location.name}</strong>`);

      // Ctrl+click to navigate to new location
      this.map.on('click', (e) => {
        if (!e.originalEvent.ctrlKey) return;
        const { lat, lng } = e.latlng;
        this.location = { lat, lon: lng, name: `${lat.toFixed(4)}, ${lng.toFixed(4)}` };
        this.locationText = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
        this.analyze();
      });

      // Station markers
      this.stationMarkerGroup = L.layerGroup().addTo(this.map);
      this.updateStationMarkers();

      // Daily obs station markers
      this.dailyObsMarkerGroup = L.layerGroup().addTo(this.map);
      this.updateDailyObsMarkers();

      // Event markers
      this.eventMarkerGroup = L.layerGroup().addTo(this.map);
      this.updateEventMarkers();
    },

    updateStationMarkers() {
      if (!this.stationMarkerGroup) return;
      this.stationMarkerGroup.clearLayers();
      if (!this.showStationMarkers || !this.stations.length) return;
      const precipUnit = this.unitGroup === 'us' ? 'in' : 'mm';
      for (const stn of this.stations) {
        if (stn.latitude == null || stn.longitude == null) continue;
        const summary = this.stationSummaries.find(s => s.id === stn.id);
        const precipLabel = summary?.loaded && summary.totalPrecip != null
          ? `<span class="swa-marker-precip">${summary.totalPrecip.toFixed(2)} ${precipUnit}</span>` : '';
        const marker = L.marker([stn.latitude, stn.longitude], {
          icon: L.divIcon({
            className: 'swa-station-icon',
            html: `<i class="bi bi-broadcast"></i>${precipLabel}`,
            iconSize: [24, 24],
            iconAnchor: [12, 12],
            popupAnchor: [0, -12],
          }),
        });
        const precipInfo = summary?.loaded ? `<br/>Precip: ${summary.totalPrecip.toFixed(2)} ${precipUnit}` : '';
        const windInfo = summary?.loaded ? `<br/>Max Gust: ${summary.maxWindGust.toFixed(1)}` : '';
        marker.bindPopup(`<strong>${stn.name || stn.id}</strong><br/>${stn.distance} ${this.distUnit} away${precipInfo}${windInfo}`);
        this.stationMarkerGroup.addLayer(marker);
      }
    },

    updateDailyObsMarkers() {
      if (!this.dailyObsMarkerGroup) return;
      this.dailyObsMarkerGroup.clearLayers();
      if (!this.showDailyObsMarkers || !this.dailyObsStations.length) return;
      const precipUnit = this.unitGroup === 'us' ? 'in' : 'mm';
      for (const stn of this.dailyObsStations) {
        if (stn.latitude == null || stn.longitude == null) continue;
        const precipLabel = stn.loaded && stn.totalPrecip != null
          ? `<span class="swa-marker-precip swa-marker-precip--daily">${stn.totalPrecip.toFixed(2)} ${precipUnit}</span>` : '';
        const marker = L.marker([stn.latitude, stn.longitude], {
          icon: L.divIcon({
            className: 'swa-daily-station-icon',
            html: `<i class="bi bi-thermometer-half"></i>${precipLabel}`,
            iconSize: [24, 24],
            iconAnchor: [12, 12],
            popupAnchor: [0, -12],
          }),
        });
        const dayDetails = stn.dayReadings.map(dr => `${dr.date}: ${dr.precip.toFixed(2)} ${precipUnit}`).join('<br/>');
        marker.bindPopup(`<strong>${stn.name}</strong><br/>${stn.distance} ${this.distUnit} away<br/>Total Precip: ${stn.totalPrecip.toFixed(2)} ${precipUnit}<br/>${dayDetails}`);
        this.dailyObsMarkerGroup.addLayer(marker);
      }
    },

    updateMapDateTime() {
      if (!this.map) return;
      const slot = this.mapTimeSlots[this.mapTimeIndex];
      if (!slot) return;
      this.map.options.dateTime = slot.date;
      this.map.fire('datetimeupdated', { dateTime: slot.date });
    },

    updateBaseLayer() {
      if (!this.map) return;
      if (this.wxBaseLayer) {
        this.map.removeLayer(this.wxBaseLayer);
        this.wxBaseLayer = null;
      }
      if (this.mapLayer === 'none') return;
      this.wxBaseLayer = new L.TileLayer.WxTileLayer({
        opacity: 0.65,
        level: 0,
        model: null,
        element: this.mapLayer,
        forceRefresh: true,
        key: this.weatherApiKey,
        unitGroup: this.unitGroup,
        dateTimeInterval: this.mapLayer === 'precipcomposite' || this.mapLayer === 'hailsize' || this.mapLayer === 'hail' ? 10 : 60,
      });
      this.wxBaseLayer.addTo(this.map);
    },

    toggleOverlayLayer(type, show) {
      if (!this.map) return;
      if (type === 'lightning') {
        if (this.wxLightningLayer) {
          this.map.removeLayer(this.wxLightningLayer);
          this.wxLightningLayer = null;
        }
        if (show) {
          this.wxLightningLayer = new L.TileLayer.WxTileLayer({
            opacity: 0.7, level: 0, model: null,
            element: 'lightning', forceRefresh: true,
            key: this.weatherApiKey, unitGroup: this.unitGroup,
            dateTimeInterval: 10,
          });
          this.wxLightningLayer.addTo(this.map);
        }
      } else if (type === 'hail') {
        if (this.wxHailLayer) {
          this.map.removeLayer(this.wxHailLayer);
          this.wxHailLayer = null;
        }
        if (show) {
          this.wxHailLayer = new L.TileLayer.WxTileLayer({
            opacity: 0.7, level: 0, model: null,
            element: 'hail', forceRefresh: true,
            key: this.weatherApiKey, unitGroup: this.unitGroup,
            dateTimeInterval: 10,
          });
          this.wxHailLayer.addTo(this.map);
        }
      }
    },

    updateEventMarkers() {
      if (!this.eventMarkerGroup) return;
      this.eventMarkerGroup.clearLayers();
      if (!this.showEventMarkers || !this.events.length) return;

      const eventIcons = {
        hail: { color: '#7c3aed', icon: 'H' },
        tornado: { color: '#dc2626', icon: 'T' },
        wind: { color: '#0891b2', icon: 'W' },
        earthquake: { color: '#b45309', icon: 'E' },
      };

      for (const evt of this.events) {
        if (evt.latitude == null || evt.longitude == null) continue;
        const cfg = eventIcons[evt.type] || { color: '#64748b', icon: '?' };
        const marker = L.circleMarker([evt.latitude, evt.longitude], {
          radius: 7, color: cfg.color, fillColor: cfg.color, fillOpacity: 0.8, weight: 2,
        });
        const popupHtml = `<strong>${evt.type.toUpperCase()}</strong><br/>
          ${evt.datetime}<br/>
          ${evt.desc || ''}<br/>
          ${this.formatEventValue(evt)}<br/>
          Distance: ${evt.distance != null ? evt.distance.toFixed(1) + ' ' + this.distUnit : '?'}`;
        marker.bindPopup(popupHtml);
        marker.bindTooltip(`${cfg.icon} ${evt.type}`, { permanent: false, direction: 'top', offset: [0, -6] });
        this.eventMarkerGroup.addLayer(marker);
      }
    },

    toggleCitiesLayer(show) {
      if (!this.map) return;
      if (this.citiesLayer) {
        this.map.removeLayer(this.citiesLayer);
        this.citiesLayer = null;
      }
      if (show) {
        this.citiesLayer = L.tileLayer('https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png', {
          subdomains: 'abcd', maxZoom: 20, pane: 'overlayPane',
        });
        this.map.addLayer(this.citiesLayer);
      }
    },

    copyApiUrl(url) {
      navigator.clipboard?.writeText(url);
    },

    stepMapTime(delta) {
      const newIdx = this.mapTimeIndex + delta;
      if (newIdx >= 0 && newIdx < this.mapTimeSlots.length) {
        this.mapTimeIndex = newIdx;
      }
    },

    togglePlay() {
      if (this.isPlaying) {
        clearInterval(this.playTimer);
        this.playTimer = null;
        this.isPlaying = false;
      } else {
        this.isPlaying = true;
        this.playTimer = setInterval(() => {
          if (this.mapTimeIndex < this.mapTimeSlots.length - 1) {
            this.mapTimeIndex++;
          } else {
            this.mapTimeIndex = 0; // loop
          }
        }, 800);
      }
    },

    // ---- Formatting helpers ----
    formatVal(val, type) {
      if (val == null || val === '') return '—';
      switch (type) {
        case 'precip': {
          const u = this.unitGroup === 'us' ? ' in' : ' mm';
          return Number(val).toFixed(2) + u;
        }
        case 'windspeed':
        case 'windgust': {
          const u = this.unitGroup === 'us' ? ' mph' : this.unitGroup === 'metric' ? ' km/h' : this.unitGroup === 'uk' ? ' mph' : ' m/s';
          return Number(val).toFixed(1) + u;
        }
        case 'dbz': return Number(val).toFixed(0) + ' dBZ';
        case 'pct': return Number(val).toFixed(0) + '%';
        case 'density': return Number(val).toFixed(2) + ' /km²';
        case 'hailsize': {
          const v = Number(val);
          if (v === 0) return '—';
          return this.unitGroup === 'us' ? v.toFixed(2) + ' in' : (v * 10).toFixed(1) + ' mm';
        }
        case 'cape': return Number(val).toFixed(0) + ' J/kg';
        case 'cin': return Number(val).toFixed(0) + ' J/kg';
        case 'helicity': return Number(val).toFixed(0) + ' m²/s²';
        case 'pressure': {
          const u = this.unitGroup === 'us' ? ' inHg' : ' hPa';
          return Number(val).toFixed(1) + u;
        }
        default: return String(val);
      }
    },

    getAlertCategory(alert) {
      const a = getAlertByType(alert.event);
      return a?.category || 'info';
    },

    getAlertIcon(alert) {
      const a = getAlertByType(alert.event);
      return getAlertIcon(a?.category || 'info');
    },

    dayRiskClass(day) {
      const sr = day.severerisk || 0;
      if (sr >= 60) return 'swa-day-card__header--danger';
      if (sr >= 30) return 'swa-day-card__header--warning';
      return '';
    },

    hourRiskClass(h) {
      const sr = h.severerisk || 0;
      if (sr >= 60) return 'swa-row--danger';
      if (sr >= 30) return 'swa-row--warning';
      return '';
    },
  },
};
</script>

<style scoped>
/* ========== Layout ========== */
.swa {
  font-family: 'Roboto', -apple-system, BlinkMacSystemFont, sans-serif;
  color: #1f2937;
  max-width: 1400px;
  margin: 0 auto;
  padding: 16px;
  font-size: 14px;
}

/* ========== Sign-in ========== */
.swa-signin {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: #fffbeb;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  color: #92400e;
}
.swa-signin i { font-size: 24px; }
.swa-signin p { margin: 2px 0 0; font-size: 13px; }

/* ========== Controls ========== */
.swa-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 16px;
}
.swa-control-group { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.swa-control-group--action { align-self: center; }
.swa-label { font-size: 11px; font-weight: 600; text-transform: uppercase; color: #64748b; letter-spacing: 0.04em; }
.swa-location-row { display: flex; gap: 4px; }
.swa-location-display { font-size: 12px; color: #475569; }
.swa-location-display i { color: #e11d48; }
.swa-date-row { display: flex; align-items: center; gap: 4px; }
.swa-date-sep { font-size: 12px; color: #94a3b8; }
.swa-quick-dates { display: flex; gap: 8px; }
.swa-quick-dates a { font-size: 11px; color: #3b82f6; text-decoration: none; }
.swa-quick-dates a:hover { text-decoration: underline; }

/* ========== Inputs ========== */
.swa-input {
  padding: 5px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 13px;
  outline: none;
  background: #fff;
}
.swa-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 2px rgba(59,130,246,0.15); }
.swa-input--location { width: 240px; }
.swa-input--date { width: 140px; }
.swa-input--select { min-width: 80px; }

/* ========== Buttons ========== */
.swa-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;
}
.swa-btn:hover { background: #f1f5f9; }
.swa-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.swa-btn--icon { padding: 5px 8px; }
.swa-btn--primary { background: #3b82f6; color: #fff; border-color: #3b82f6; }
.swa-btn--primary:hover { background: #2563eb; }
.swa-btn--analyze { font-weight: 600; }
@keyframes swa-spin { to { transform: rotate(360deg); } }
.swa-spin { animation: swa-spin 0.8s linear infinite; }

/* ========== Skeleton Loading ========== */
@keyframes swa-shimmer {
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
}
.swa-skeleton { margin-bottom: 16px; }
.swa-skeleton-card {
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
}
.swa-skeleton-bar {
  background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
  background-size: 800px 100%;
  animation: swa-shimmer 1.5s infinite ease-in-out;
  border-radius: 4px;
}
.swa-skeleton-bar--icon { width: 32px; height: 32px; border-radius: 50%; margin: 0 auto 8px; }
.swa-skeleton-bar--value { width: 60%; height: 20px; margin: 0 auto 6px; }
.swa-skeleton-bar--label { width: 80%; height: 12px; margin: 0 auto; }
.swa-skeleton-bar--cell { width: 70%; height: 14px; margin: 4px auto; }
.swa-skeleton-bar--day-header { width: 40%; height: 18px; margin-bottom: 8px; }
.swa-skeleton-bar--day-body { width: 100%; height: 60px; }
.swa-skeleton-day { margin-bottom: 12px; }

/* ========== Error ========== */
.swa-error {
  padding: 10px 14px;
  background: #fef2f2;
  border: 1px solid #fca5a5;
  border-radius: 6px;
  color: #991b1b;
  margin-bottom: 16px;
  font-size: 13px;
}

/* ========== Section ========== */
.swa-section {
  margin-bottom: 20px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}
.swa-section__title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 10px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 15px;
  font-weight: 600;
  color: #334155;
}
.swa-section__title i { color: #3b82f6; }

/* ========== KPI Grid ========== */
.swa-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  padding: 16px;
}
.swa-kpi {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 8px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  text-align: center;
  transition: transform 0.15s;
}
.swa-kpi:hover { transform: translateY(-2px); }
.swa-kpi__icon { font-size: 20px; color: #64748b; margin-bottom: 4px; }
.swa-kpi__value { font-size: 22px; font-weight: 700; color: #1e293b; }
.swa-kpi__label { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.03em; margin-top: 2px; }
.swa-kpi--danger { background: #fef2f2; border-color: #fca5a5; }
.swa-kpi--danger .swa-kpi__icon { color: #dc2626; }
.swa-kpi--danger .swa-kpi__value { color: #dc2626; }
.swa-kpi--warning { background: #fffbeb; border-color: #fcd34d; }
.swa-kpi--warning .swa-kpi__icon { color: #d97706; }
.swa-kpi--warning .swa-kpi__value { color: #d97706; }
.swa-kpi--safe { background: #f0fdf4; border-color: #86efac; }
.swa-kpi--safe .swa-kpi__icon { color: #16a34a; }
.swa-kpi--safe .swa-kpi__value { color: #16a34a; }

/* ========== Alerts ========== */
.swa-alerts { padding: 12px 16px; display: flex; flex-direction: column; gap: 10px; }
.swa-alert {
  padding: 10px 14px;
  border-radius: 6px;
  border-left: 4px solid #94a3b8;
  background: #f8fafc;
}
.swa-alert--danger { border-left-color: #dc2626; background: #fef2f2; }
.swa-alert--warning { border-left-color: #d97706; background: #fffbeb; }
.swa-alert--info { border-left-color: #0284c7; background: #f0f9ff; }
.swa-alert__header { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.swa-alert__time { font-size: 11px; color: #64748b; margin-left: auto; }
.swa-alert__headline { font-size: 13px; margin: 6px 0 0; color: #334155; }
.swa-alert__details { margin-top: 6px; font-size: 12px; color: #475569; }
.swa-alert__details summary { cursor: pointer; font-weight: 600; }

/* ========== Comparison Table ========== */
.swa-comparison-table-wrap { overflow-x: auto; padding: 12px 16px; }
.swa-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.swa-table th, .swa-table td { padding: 8px 10px; border-bottom: 1px solid #e2e8f0; text-align: left; }
.swa-table th { background: #f8fafc; font-weight: 600; color: #475569; font-size: 12px; text-transform: uppercase; letter-spacing: 0.03em; }
.swa-table__el { font-weight: 600; }
.swa-table__note { font-size: 12px; color: #64748b; max-width: 250px; }
.swa-table__src { font-size: 11px; color: #94a3b8; }
.swa-table__summary-row { background: #f8fafc; border-top: 2px solid #e2e8f0; }
.swa-table__summary-row .swa-table__el .swa-summary-label { display: inline-flex; align-items: center; gap: 6px; }
.swa-delta--warn { color: #dc2626; font-weight: 600; }

/* ========== Source Legend ========== */
.swa-source-legend { display: flex; flex-wrap: wrap; gap: 16px; padding: 12px 16px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; font-size: 12px; color: #475569; }
.swa-source-legend__item { display: flex; align-items: center; gap: 6px; }
.swa-source-dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.swa-source-dot--station { background: #3b82f6; }
.swa-source-dot--radar { background: #16a34a; }
.swa-source-dot--map { background: #7c3aed; }
.swa-source-dot--era5 { background: #d97706; }
.swa-source-dot--v1forecast { background: #e11d48; }
.swa-source-dot--dailyobs { background: #0891b2; }

/* ========== Station Breakdown ========== */
.swa-station-breakdown { border-top: 1px solid #e2e8f0; }
.swa-subsection-title { display: flex; align-items: center; gap: 8px; margin: 0; padding: 10px 16px; font-size: 14px; font-weight: 600; color: #334155; }
.swa-subsection-title i { color: #3b82f6; }
.swa-station-breakdown__desc { margin: 0 16px 8px; font-size: 12px; color: #64748b; }
.swa-station-id { font-size: 11px; color: #94a3b8; font-weight: 400; }
.swa-loading-inline { font-size: 12px; font-weight: 400; color: #64748b; margin-left: 8px; }
.swa-row--loading td { color: #94a3b8; }
.swa-no-data { padding: 12px 16px; font-size: 13px; color: #94a3b8; margin: 0; }

/* ========== Station Cards ========== */
.swa-station-cards { display: flex; flex-wrap: wrap; gap: 10px; padding: 12px 16px; }
.swa-station-card {
  flex: 1 1 260px;
  min-width: 260px;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
}
.swa-station-card__header { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
.swa-station-card__header i { color: #3b82f6; }
.swa-station-card__dist { margin-left: auto; font-size: 12px; font-weight: 700; color: #0369a1; }
.swa-station-card__meta { display: flex; flex-wrap: wrap; gap: 10px; font-size: 12px; color: #64748b; }
.swa-station-card__meta i { color: #94a3b8; }

/* ========== Day Cards ========== */
.swa-day-card {
  border-bottom: 1px solid #e2e8f0;
}
.swa-day-card:last-child { border-bottom: none; }
.swa-day-card__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: #f8fafc;
  flex-wrap: wrap;
}
.swa-day-card__header--warning { background: #fffbeb; }
.swa-day-card__header--danger { background: #fef2f2; }
.swa-day-card__date { font-weight: 700; font-size: 14px; }
.swa-day-card__badge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  background: #fca5a5;
  color: #7f1d1d;
}
.swa-day-card__desc { font-size: 12px; color: #64748b; margin-left: auto; }
.swa-day-card__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 6px;
  padding: 10px 16px;
}
.swa-day-stat {
  display: flex;
  flex-direction: column;
}
.swa-day-stat__label { font-size: 10px; text-transform: uppercase; color: #94a3b8; letter-spacing: 0.03em; }
.swa-day-stat__val { font-size: 14px; font-weight: 600; color: #1e293b; }

/* ========== Hourly table ========== */
.swa-day-card__hourly { padding: 0 16px 12px; }
.swa-day-card__hourly summary { font-size: 12px; font-weight: 600; color: #3b82f6; cursor: pointer; margin-bottom: 6px; }
.swa-hourly-table-wrap { overflow-x: auto; }
.swa-table--compact td, .swa-table--compact th { padding: 4px 6px; font-size: 12px; }
.swa-row--warning { background: #fffbeb; }
.swa-row--danger { background: #fef2f2; }

/* ========== Events ========== */
.swa-events-table-wrap { overflow-x: auto; padding: 12px 16px; }
.swa-event-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #fff;
  background: #64748b;
}
.swa-event-badge--hail { background: #7c3aed; }
.swa-event-badge--tornado { background: #dc2626; }
.swa-event-badge--wind { background: #0891b2; }
.swa-event-badge--earthquake { background: #b45309; }
.swa-event-row--hail { background: #f5f3ff; }
.swa-event-row--tornado { background: #fef2f2; }
.swa-event-row--wind { background: #ecfeff; }
.swa-table__nowrap { white-space: nowrap; }

/* ========== Map ========== */
.swa-map-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-bottom: 1px solid #e2e8f0;
  flex-wrap: wrap;
}
.swa-label--overlay {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  text-transform: none;
  cursor: pointer;
}
.swa-label--overlay input[type="checkbox"] { margin: 0; cursor: pointer; }
.swa-map-time-controls {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
}
.swa-map-time-label {
  font-size: 13px;
  font-weight: 600;
  min-width: 180px;
  text-align: center;
  color: #1e293b;
}
.swa-map-slider-wrap { width: 100%; padding: 0 4px; }
.swa-map-slider {
  width: 100%;
  height: 6px;
  -webkit-appearance: none;
  appearance: none;
  background: #e2e8f0;
  border-radius: 3px;
  outline: none;
  cursor: pointer;
}
.swa-map-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
}
.swa-map {
  height: 450px;
  width: 100%;
}
.swa-map :deep(.swa-location-icon) {
  background: none !important;
  border: none !important;
  display: flex !important;
  align-items: flex-end;
  justify-content: center;
}
.swa-map :deep(.swa-location-icon .bi-geo-alt-fill) {
  font-size: 32px;
  color: #e11d48;
  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.4));
  line-height: 1;
}
.swa-map :deep(.swa-station-icon) {
  background: none !important;
  border: none !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
}
.swa-map :deep(.swa-station-icon .bi-broadcast) {
  font-size: 20px;
  color: #3b82f6;
  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.5));
  line-height: 1;
}
.swa-map :deep(.swa-daily-station-icon) {
  background: none !important;
  border: none !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
}
.swa-map :deep(.swa-daily-station-icon .bi-thermometer-half) {
  font-size: 20px;
  color: #0891b2;
  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.5));
  line-height: 1;
}
.swa-map :deep(.swa-marker-precip) {
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(59, 130, 246, 0.9);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 4px;
  border-radius: 3px;
  white-space: nowrap;
  line-height: 1.2;
  pointer-events: none;
}
.swa-map :deep(.swa-marker-precip--daily) {
  background: rgba(8, 145, 178, 0.9);
}

/* ========== API Calls ========== */
.swa-api-calls { padding: 12px 16px; display: flex; flex-direction: column; gap: 8px; }
.swa-api-call { border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 12px; background: #f8fafc; }
.swa-api-call__label { font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 4px; }
.swa-api-call__url-row { display: flex; align-items: center; gap: 6px; }
.swa-api-call__url { flex: 1; font-size: 11px; color: #334155; word-break: break-all; background: #fff; padding: 4px 8px; border: 1px solid #e2e8f0; border-radius: 4px; }
.swa-api-call__copy { flex-shrink: 0; }

/* ========== Daily Temp Inline ========== */
.swa-daily-temp { display: block; font-size: 11px; color: #64748b; }

/* ========== Source Agreement Panel ========== */
.swa-agreement-panel { border-top: 1px solid #e2e8f0; padding: 12px 16px; }
.swa-agreement-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px; margin-top: 8px; }
.swa-agreement-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; }
.swa-agreement-card__title { font-weight: 600; font-size: 13px; color: #334155; margin-bottom: 4px; }
.swa-agreement-card__range { font-size: 16px; font-weight: 700; color: #1e293b; }
.swa-agreement-card__avg { font-size: 12px; color: #64748b; }
.swa-agreement-card__spread { font-size: 12px; color: #64748b; margin-bottom: 6px; }
.swa-agreement-card__sources { display: flex; flex-wrap: wrap; gap: 6px; margin: 6px 0; }
.swa-agreement-source { display: inline-flex; align-items: center; gap: 3px; font-size: 11px; color: #475569; }
.swa-agreement-card__verdict { font-size: 12px; font-weight: 600; margin-top: 4px; }
.swa-agreement--good { border-color: #22c55e; }
.swa-agreement--good .swa-agreement-card__verdict { color: #16a34a; }
.swa-agreement--moderate { border-color: #f59e0b; }
.swa-agreement--moderate .swa-agreement-card__verdict { color: #d97706; }
.swa-agreement--poor { border-color: #ef4444; }
.swa-agreement--poor .swa-agreement-card__verdict { color: #dc2626; }

/* ========== Responsive ========== */
@media (max-width: 768px) {
  .swa-controls { flex-direction: column; }
  .swa-input--location { width: 100%; }
  .swa-kpi-grid { grid-template-columns: repeat(2, 1fr); }
  .swa-day-card__grid { grid-template-columns: repeat(2, 1fr); }
  .swa-map { height: 300px; }
}
</style>
